/**
 * SMS API client — calls Supabase Edge Functions (sms-api, sms-webhook, etc.)
 * with multi-tenant businessId.
 */

import { supabase } from "@/integrations/supabase/client";
import { logActivity, resolveUser } from "@/lib/activityLogger";
import type {
    SmsCampaign,
    SmsLead,
    SmsThreadWithLead,
    SmsMessage,
    SmsAppointmentWithLead,
    SmsCampaignCost,
    SmsCostOverview,
    SmsDashboardData,
    SmsCSVUploadResult,
    SmsLeadDetail,
    SmsSettings,
    TwilioNumberUsage,
    ReassignmentPlan,
    TwilioBalance,
} from "./sms-types";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const BASE = `${SUPABASE_URL}/functions/v1/sms-api`;

/** Build auth headers for an sms-api call.
 *
 *  RBAC-gated routes (e.g. `/campaigns/:id/pause`, `/campaigns/:id/start`)
 *  run `requirePermission` server-side, which calls
 *  `supabase.auth.getUser(token)`. That call only resolves the caller when
 *  the Bearer token is the *user's* session access_token — passing the
 *  project anon key yields "Invalid session" → 401. Mirrors the pattern
 *  already used by `dedup-api.ts` and `activityLogger.ts`. */
async function authHeaders(): Promise<HeadersInit> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token ?? SUPABASE_ANON_KEY;
    return {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        "apikey": SUPABASE_ANON_KEY,
    };
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
        ...options,
        headers: {
            ...(await authHeaders()),
            ...(options?.headers ?? {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || res.statusText);
    }
    return res.json() as Promise<T>;
}

/** Convert snake_case API response keys to camelCase for frontend types */
/** Map DB snake_case keys to the camelCase names our frontend types expect.
 *  Most keys auto-convert (e.g. first_name → firstName).
 *  Add explicit overrides here when the DB column doesn't match 1:1. */
const FIELD_ALIASES: Record<string, string> = {
    sms_status: 'status',
    phone: 'phone1',
};

function snakeToCamel<T>(obj: Record<string, unknown>): T {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        const camelKey = FIELD_ALIASES[key] ?? key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
        result[camelKey] = value;
    }
    return result as T;
}

// ── Campaigns ──
export const getSmsCampaigns = (businessId: string) =>
    request<Record<string, unknown>[]>(`/campaigns?businessId=${businessId}`)
        .then(rows => rows.map(r => snakeToCamel<SmsCampaign>(r)));

export const getSmsCampaignsArchived = (businessId: string) =>
    request<Record<string, unknown>[]>(`/campaigns?businessId=${businessId}&archived=true`)
        .then(rows => rows.map(r => snakeToCamel<SmsCampaign>(r)));


export const getSmsCampaign = (id: string) =>
    request<Record<string, unknown>>(`/campaigns/${id}`)
        .then(r => snakeToCamel<SmsCampaign & { totalLeads: number; deadThreads: number }>(r));

export const createSmsCampaign = (data: Partial<SmsCampaign> & { businessId: string }) =>
    request<Record<string, unknown>>("/campaigns", { method: "POST", body: JSON.stringify(data) })
        .then(r => snakeToCamel<SmsCampaign>(r));

export const updateSmsCampaign = (id: string, data: Partial<SmsCampaign>) =>
    request<Record<string, unknown>>(`/campaigns/${id}`, { method: "PUT", body: JSON.stringify(data) })
        .then(r => snakeToCamel<SmsCampaign>(r));

export const archiveSmsCampaign = (id: string, businessId: string) =>
    request<{ success: boolean }>(`/campaigns/${id}?businessId=${businessId}`, { method: "DELETE" });

export const startSmsCampaign = (id: string, businessId: string) =>
    request<{ success: boolean; message: string; newLeads: number; withinHours: boolean }>(
        `/campaigns/${id}/start`,
        { method: "POST", body: JSON.stringify({ businessId }) }
    );

export const pauseSmsCampaign = (id: string, businessId: string) =>
    request<{ success: boolean; message: string }>(
        `/campaigns/${id}/pause`,
        { method: "POST", body: JSON.stringify({ businessId }) }
    );

// ── Leads ──
export interface SmsLeadWithCampaign extends SmsLead {
    campaignName: string;
    /** Server-enriched pipeline + stage display fields (joined from
     *  lead_pipelines / lead_pipeline_stages). Power the inline colored-chip
     *  dropdown and the "Pipeline" column. */
    leadPipelineId: string | null;
    leadPipelineName: string | null;
    leadPipelineStageId: string | null;
    leadPipelineStageName: string | null;
    leadPipelineStageColor: string | null;
    leadPipelineStageIcon: string | null;
}

/** Full pipeline-stage record returned alongside the leads payload so the
 *  list view can render a colored-chip dropdown without an extra fetch. */
export interface PipelineStageOption {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
    pipeline_id: string | null;
    sort_order: number;
    is_active: boolean;
}

export const getSmsLeads = (campaignId: string, params?: { status?: string; search?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.search) query.set("search", params.search);
    query.set("limit", String(params?.limit ?? 10000));
    query.set("offset", String(params?.offset ?? 0));
    const qs = query.toString();
    return request<{ leads: Record<string, unknown>[]; total: number }>(`/campaigns/${campaignId}/leads${qs ? `?${qs}` : ""}`)
        .then(r => ({ leads: r.leads.map(l => snakeToCamel<SmsLead>(l)), total: r.total }));
};

export interface GetAllSmsLeadsParams {
    search?: string;
    status?: string | string[];
    campaignId?: string | string[];
    source?: string | string[];
    state?: string | string[];
    /** Mixed area selection — string[] can contain numeric ids (drawn
     *  imported_area_id values) or uuids (manual crm_areas ids). The edge
     *  function routes each to the right column. */
    area?: string | string[];
    /** UUID-based area_id for manual areas (single value, from props) */
    areaUuid?: string;
    /** Drawn-area territory UUID (PR #371). Edge function ORs this with the
     *  drawn-area `area` filter so leads attached either via `territory_id`
     *  (modern) or `imported_area_id` (legacy "IM Area NNN") both appear. */
    territoryId?: string;
    leadType?: string | string[];
    pipelineStageId?: string | string[];
    pipelineId?: string | string[];
    /** Projects module: when true, return converted clients (is_client=true)
     *  instead of leads. Backs the Clients view, which reuses this exact
     *  list endpoint. Defaults to false (leads only). */
    isClient?: boolean;
    /** Phase 3 RBAC: restrict to leads created by a specific user (for the
     *  "Only mine" UI toggle). Enforced server-side for accurate pagination. */
    createdBy?: string;
    /** Phase 2 hierarchy: restrict to leads created by any of these user_ids
     *  (for "Subtree" scope on the CRM dropdown). Sent server-side as a CSV
     *  so pagination stays accurate. Takes precedence over createdBy when both
     *  are set. */
    createdByIds?: string[];
    /** Sales-assist filter — restrict to leads where assist_user_id IS IN
     *  the given user_ids. Server-side so pagination stays accurate. */
    assistUserIds?: string[];
    /** Explicit owner picker — restrict to leads where created_by IS IN the
     *  given user_ids. ANDs with the scope-derived createdBy/createdByIds so
     *  a user can refine "my subtree" down to specific teammates without
     *  losing the visibility gate. Server-side for accurate pagination. */
    ownerUserIds?: string[];
    /** Mimic: when an owner/admin is "viewing as" a member, this is the
     *  mimicked user's id. The server verifies the caller may mimic, then
     *  scopes the list EXACTLY as that member would see it. */
    mimicUserId?: string;
    /** Duplicates filter — when true, the server resolves the flagged-ids
     *  set through the shared dedup engine and restricts the query to
     *  those rows so pagination + counts stay accurate. */
    dupOnly?: boolean;
    /** Ownership gate for the dup filter (set when the user can't view
     *  all records). Mirrors the dedup-api `ownedByUserId` param. */
    dupOwnedBy?: string | null;
    /** ISO timestamps for the "Updated within" filter — the edge function
     *  compares against `leads.last_activity_at` (denormalized column
     *  bumped by the per-tab triggers in 20260513120000_leads_last_activity). */
    updatedAfter?: string;
    updatedBefore?: string;
    /** "Last update type" multi-select. Values: 'note' | 'task' | 'call' |
     *  'sms' | 'meeting' | 'energy_analysis' | 'pipeline_stage' |
     *  'disposition' | 'lead_edit'. */
    lastActivityType?: string | string[];
    /** Custom-field filter allow-list. Client resolves the matching lead-id
     *  set against `lead_custom_field_values` before calling, then passes
     *  the ids as `leadIdsAllow`. Pass the literal `"__none__"` to signal
     *  "filters active but nothing matches" so the server short-circuits. */
    leadIdsAllow?: string[] | "__none__";
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
}

/** Build the shared `/leads` query string used by BOTH the paginated list
 *  (getAllSmsLeads) and the id-only "select all matching" fetch
 *  (getAllSmsLeadIds). One builder means the filter handling can never
 *  drift between the two — critical so "Select all N" selects exactly the
 *  rows the list shows. `limit`/`offset`/`idsOnly` are appended by callers. */
const buildLeadsQuery = (businessId: string, params?: GetAllSmsLeadsParams): URLSearchParams => {
    const query = new URLSearchParams({ businessId });
    if (params?.search) query.set("search", params.search);
    const statusVal = Array.isArray(params?.status) ? params.status.join(",") : params?.status;
    const campaignVal = Array.isArray(params?.campaignId) ? params.campaignId.join(",") : params?.campaignId;
    const sourceVal = Array.isArray(params?.source) ? params.source.join(",") : params?.source;
    const stateVal = Array.isArray(params?.state) ? params.state.join(",") : params?.state;
    const areaVal = Array.isArray(params?.area) ? params.area.join(",") : params?.area;
    const leadTypeVal = Array.isArray(params?.leadType) ? params.leadType.join(",") : params?.leadType;
    const pipelineStageVal = Array.isArray(params?.pipelineStageId) ? params.pipelineStageId.join(",") : params?.pipelineStageId;
    const pipelineVal = Array.isArray(params?.pipelineId) ? params.pipelineId.join(",") : params?.pipelineId;
    if (statusVal) query.set("status", statusVal);
    if (campaignVal) query.set("campaignId", campaignVal);
    if (sourceVal) query.set("source", sourceVal);
    if (stateVal) query.set("state", stateVal);
    if (areaVal) query.set("area", areaVal);
    if (params?.areaUuid) query.set("areaUuid", params.areaUuid);
    if (params?.territoryId) query.set("territoryId", params.territoryId);
    if (leadTypeVal) query.set("leadType", leadTypeVal);
    if (pipelineStageVal) query.set("pipelineStageId", pipelineStageVal);
    if (pipelineVal) query.set("pipelineId", pipelineVal);
    if (params?.isClient) query.set("isClient", "1");
    if (params?.createdByIds && params.createdByIds.length > 0) {
        query.set("createdByIds", params.createdByIds.join(","));
    } else if (params?.createdBy) {
        query.set("createdBy", params.createdBy);
    }
    if (params?.assistUserIds && params.assistUserIds.length > 0) {
        query.set("assistUserIds", params.assistUserIds.join(","));
    }
    if (params?.ownerUserIds && params.ownerUserIds.length > 0) {
        query.set("ownerUserIds", params.ownerUserIds.join(","));
    }
    if (params?.mimicUserId) query.set("mimicUserId", params.mimicUserId);
    if (params?.dupOnly) query.set("dupOnly", "1");
    if (params?.dupOwnedBy) query.set("dupOwnedBy", params.dupOwnedBy);
    if (params?.updatedAfter) query.set("updatedAfter", params.updatedAfter);
    if (params?.updatedBefore) query.set("updatedBefore", params.updatedBefore);
    const lastActivityTypeVal = Array.isArray(params?.lastActivityType)
        ? params.lastActivityType.join(",")
        : params?.lastActivityType;
    if (lastActivityTypeVal) query.set("lastActivityType", lastActivityTypeVal);
    if (params?.leadIdsAllow === "__none__") {
        query.set("leadIdsAllow", "__none__");
    } else if (Array.isArray(params?.leadIdsAllow) && params.leadIdsAllow.length > 0) {
        query.set("leadIdsAllow", params.leadIdsAllow.join(","));
    }
    if (params?.sortBy) query.set("sortBy", params.sortBy);
    if (params?.sortDir) query.set("sortDir", params.sortDir);
    return query;
};

/** Raw /leads payload — identical shape from the edge function and the
 *  get_leads_page RPC (the RPC is a faithful port of the edge route). */
interface RawLeadsResponse {
    leads: Record<string, unknown>[];
    total: number;
    campaigns: { id: string; name: string }[];
    sources: string[];
    states: string[];
    /** Mixed manual + drawn areas — manual first (sorted by created_at
     *  DESC), drawn second (sorted by imported_area_id DESC). Value is
     *  a uuid for manual or the imported_area_id as a string for drawn. */
    areas: { value: string; label: string }[];
    leadTypes: string[];
    pipelineStages: PipelineStageOption[];
    pipelines: { id: string; name: string }[];
}

const mapLeadsResponse = (r: RawLeadsResponse) => ({
    leads: r.leads.map(l => snakeToCamel<SmsLeadWithCampaign>(l)),
    total: r.total,
    campaigns: r.campaigns ?? [],
    sources: r.sources ?? [],
    states: r.states ?? [],
    areas: r.areas ?? [],
    leadTypes: r.leadTypes ?? [],
    pipelineStages: r.pipelineStages ?? [],
    pipelines: r.pipelines ?? [],
});

/** Killswitch: set localStorage.nk_leads_rpc_off = "1" to force every leads
 *  fetch back onto the sms-api edge function (same idiom as nk_home_rpc_off). */
const LEADS_RPC_OFF_KEY = "nk_leads_rpc_off";
const leadsRpcDisabled = (): boolean => {
    try { return localStorage.getItem(LEADS_RPC_OFF_KEY) === "1"; } catch { return false; }
};

/** Call the get_leads_page RPC (no edge-function cold start). Mirrors
 *  buildLeadsQuery's param handling — single string values may be internal
 *  CSVs (the edge fn splits on ','), so normalize via join+split. */
async function getLeadsPageViaRpc(businessId: string, params?: GetAllSmsLeadsParams): Promise<RawLeadsResponse> {
    const csv = (v?: string | string[]): string[] | null => {
        const joined = Array.isArray(v) ? v.join(",") : (v ?? "");
        const list = joined.split(",").filter(Boolean);
        return list.length ? list : null;
    };
    const ids = (v?: string[]): string[] | null => (v && v.length > 0 ? v : null);
    const { data, error } = await supabase.rpc("get_leads_page" as never, {
        p_business_id: businessId,
        p_search: params?.search || null,
        p_status: csv(params?.status),
        p_campaign_id: csv(params?.campaignId),
        p_source: csv(params?.source),
        p_state: csv(params?.state),
        p_area: csv(params?.area),
        p_area_uuid: params?.areaUuid || null,
        p_territory_id: params?.territoryId || null,
        p_lead_type: csv(params?.leadType),
        p_pipeline_stage_id: csv(params?.pipelineStageId),
        p_pipeline_id: csv(params?.pipelineId),
        p_is_client: !!params?.isClient,
        // createdByIds takes precedence over createdBy (matches the edge fn)
        p_created_by: params?.createdByIds && params.createdByIds.length > 0 ? null : (params?.createdBy || null),
        p_created_by_ids: ids(params?.createdByIds),
        p_assist_user_ids: ids(params?.assistUserIds),
        p_owner_user_ids: ids(params?.ownerUserIds),
        p_mimic_user_id: params?.mimicUserId || null,
        p_updated_after: params?.updatedAfter || null,
        p_updated_before: params?.updatedBefore || null,
        p_last_activity_type: csv(params?.lastActivityType),
        p_lead_ids_allow: Array.isArray(params?.leadIdsAllow) && params.leadIdsAllow.length > 0 ? params.leadIdsAllow : null,
        p_allow_none: params?.leadIdsAllow === "__none__",
        p_sort_by: params?.sortBy || "created_at",
        p_sort_dir: params?.sortDir || "desc",
        p_limit: params?.limit ?? 50,
        p_offset: params?.offset ?? 0,
    } as never);
    if (error) throw error;
    return data as unknown as RawLeadsResponse;
}

export const getAllSmsLeads = async (businessId: string, params?: GetAllSmsLeadsParams) => {
    // The dupOnly path stays on the edge function — its in-process dedup
    // grouping (phone/email normalization + owner gate) is not ported to SQL.
    if (!params?.dupOnly && !leadsRpcDisabled()) {
        try {
            return mapLeadsResponse(await getLeadsPageViaRpc(businessId, params));
        } catch (err) {
            console.warn("[leads] get_leads_page RPC failed — falling back to sms-api edge fn:", err);
        }
    }
    const query = buildLeadsQuery(businessId, params);
    query.set("limit", String(params?.limit ?? 50));
    query.set("offset", String(params?.offset ?? 0));
    return request<RawLeadsResponse>(`/leads?${query.toString()}`).then(mapLeadsResponse);
};

/** Every matching lead id for the current filter — no pagination. Backs the
 *  Leads list "Select all N matching this filter" → bulk-edit flow (e.g.
 *  dropping thousands of leads into an area in one shot). The edge function
 *  loops past PostgREST's 1000-row cap so the id set is complete. Uses the
 *  exact same filter builder as getAllSmsLeads, so the selection matches the
 *  visible list 1:1. */
export const getAllSmsLeadIds = (businessId: string, params?: GetAllSmsLeadsParams) => {
    const query = buildLeadsQuery(businessId, params);
    query.set("idsOnly", "1");
    return request<{ ids: string[]; total: number }>(`/leads?${query.toString()}`);
};

export const uploadSmsCsv = (campaignId: string, csvData: string) =>
    request<SmsCSVUploadResult>(`/campaigns/${campaignId}/leads/upload`, {
        method: "POST",
        body: JSON.stringify({ csvData }),
    });

export const mapAndImportSmsLeads = (campaignId: string, csvData: string, mappings: Record<string, string>, businessId: string) =>
    request<{ success: boolean; imported: number }>(`/campaigns/${campaignId}/leads/map`, {
        method: "POST",
        body: JSON.stringify({ csvData, mappings, businessId }),
    });

export const pasteSmsLeads = (campaignId: string, tableData: string) =>
    request<SmsCSVUploadResult & { parsedData: string }>(`/campaigns/${campaignId}/leads/paste`, {
        method: "POST",
        body: JSON.stringify({ tableData }),
    });

export const getSmsLead = (id: string) =>
    request<Record<string, unknown>>(`/leads/${id}`)
        .then(r => {
            const out: Record<string, unknown> = {};
            // Transform top-level keys
            for (const [key, value] of Object.entries(r)) {
                const camelKey = key.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());
                if (value && typeof value === "object" && !Array.isArray(value)) {
                    out[camelKey] = snakeToCamel<Record<string, unknown>>(value as Record<string, unknown>);
                } else if (Array.isArray(value)) {
                    out[camelKey] = value.map(item =>
                        typeof item === "object" && item ? snakeToCamel<Record<string, unknown>>(item as Record<string, unknown>) : item
                    );
                } else {
                    out[camelKey] = value;
                }
            }
            return out as unknown as SmsLeadDetail;
        });

export const updateSmsLead = (id: string, data: Partial<SmsLead>) =>
    request<Record<string, unknown>>(`/leads/${id}`, { method: "PUT", body: JSON.stringify(data) })
        .then(r => snakeToCamel<SmsLead>(r));

export const archiveSmsLead = (id: string) =>
    request<{ success: boolean }>(`/leads/${id}`, { method: "DELETE" });

/** Reset a lead from error state so the campaign picks it back up */
export const restartSmsLead = (id: string) =>
    request<{ success: boolean }>(`/leads/${id}/restart`, { method: "POST" });

export const bulkArchiveSmsLeads = (ids: string[]) =>
    request<{ success: boolean; deleted: number }>("/leads/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) });

// ── Threads ──
export const getSmsThreads = (campaignId: string) =>
    request<Record<string, unknown>[]>(`/campaigns/${campaignId}/threads`)
        .then(rows => rows.map(r => snakeToCamel<SmsThreadWithLead>(r)));

export const getSmsThread = (id: string) =>
    request<Record<string, unknown>>(`/threads/${id}`)
        .then(r => ({
            thread: snakeToCamel<SmsThreadWithLead>(r.thread as Record<string, unknown>),
            messages: (r.messages as Record<string, unknown>[]).map(m => snakeToCamel<SmsMessage>(m)),
        }));

export const sendSmsManualReply = (
    threadId: string,
    body: string,
    businessId: string,
    fromNumberId?: string,
    mediaUrls?: string[],
    activityTarget?: { type: string; id: string; label?: string },
) =>
    request<{ success: boolean; sid: string }>(`/threads/${threadId}/reply`, {
        method: "POST",
        body: JSON.stringify({ body, businessId, fromNumberId, mediaUrls }),
    }).then((res) => {
        // Fire-and-forget activity trail on success — activity_logs is the
        // ONLY per-user record of an outbound text (sms_messages carries no
        // user attribution), and every send surface funnels through here.
        void resolveUser(businessId)
            .then((user) => {
                if (!user) return;
                logActivity({
                    businessId,
                    userId: user.userId,
                    userName: user.userName,
                    action: "sms_sent",
                    entityType: "sms",
                    entityLabel: body.slice(0, 80),
                    targetType: activityTarget?.type,
                    targetId: activityTarget?.id,
                    targetLabel: activityTarget?.label,
                });
            })
            .catch(() => { /* never block or fail a send on logging */ });
        return res;
    });

/** Phase 4: create-or-get the direct (non-campaign) thread for a lead. Delegates
 *  to the sms-api edge function so the server-side CHECK constraints and unique
 *  index on `sms_threads` are the source of truth. */
export const getOrCreateDirectThread = (leadId: string, businessId: string) =>
    request<Record<string, unknown>>(`/threads/direct`, {
        method: "POST",
        body: JSON.stringify({ leadId, businessId }),
    });

/** Resolve which Twilio number to default the lead-detail Dialer/SMS surfaces
 *  to: the most recent campaign thread's outbound number, or null if the lead
 *  has no campaign thread (caller falls back to business-main / member default). */
export const getRecentCampaignFromNumberId = (leadId: string, businessId: string) =>
    request<{ twilioNumberId: string | null }>(
        `/leads/${leadId}/recent-campaign-from-number?businessId=${encodeURIComponent(businessId)}`
    ).then(r => r.twilioNumberId);

// ── Appointments ──
export const getSmsAppointments = (businessId: string, params?: { status?: string; campaignId?: string; leadId?: string }) => {
    const query = new URLSearchParams({ businessId });
    if (params?.status) query.set("status", params.status);
    if (params?.campaignId) query.set("campaignId", params.campaignId);
    if (params?.leadId) query.set("leadId", params.leadId);
    return request<Record<string, unknown>[]>(`/appointments?${query.toString()}`)
        .then(rows => rows.map(r => snakeToCamel<SmsAppointmentWithLead>(r)));
};

export const updateSmsAppointment = (id: string, data: Partial<SmsAppointmentWithLead>) =>
    request<Record<string, unknown>>(`/appointments/${id}`, { method: "PUT", body: JSON.stringify(data) })
        .then(r => snakeToCamel<SmsAppointmentWithLead>(r));

// ── Costs ──
export const getSmsCampaignCosts = (campaignId: string) =>
    request<Record<string, unknown>>(`/campaigns/${campaignId}/costs`)
        .then(r => snakeToCamel<SmsCampaignCost>(r));

export const getSmsCostOverview = (businessId: string) =>
    request<Record<string, unknown>>(`/costs/overview?businessId=${businessId}`)
        .then(r => snakeToCamel<SmsCostOverview>(r));

// ── Dashboard ──
export const getSmsDashboard = (businessId: string) =>
    request<Record<string, unknown>>(`/dashboard?businessId=${businessId}`)
        .then(r => snakeToCamel<SmsDashboardData>(r));

// ── Settings ──
export const getSmsSettings = (businessId: string) =>
    request<Record<string, unknown>>(`/settings?businessId=${businessId}`)
        .then(r => snakeToCamel<SmsSettings>(r));

export const saveSmsSettings = (data: Partial<SmsSettings> & { businessId: string }) =>
    request<SmsSettings>("/settings", { method: "POST", body: JSON.stringify(data) });

// ── Test ──
export const testSmsAI = (businessId: string) =>
    request<{ success: boolean; message: string }>("/test/ai", { method: "POST", body: JSON.stringify({ businessId }) });

export const testSmsTwilio = (businessId: string) =>
    request<{ success: boolean; message: string }>("/test/twilio", { method: "POST", body: JSON.stringify({ businessId }) });

export const testSmsSend = (businessId: string, phone: string) =>
    request<{ success: boolean; message: string }>("/test/sms", { method: "POST", body: JSON.stringify({ businessId, phone }) });

export const testLead = (data: {
    campaignId: string; firstName: string; lastName: string; phone: string;
    email?: string; street?: string; city?: string; state?: string; zip?: string;
}) =>
    request<{ success: boolean; leadId: string; message: string }>("/test-lead", { method: "POST", body: JSON.stringify(data) });

// ── Archive (Leads, Threads, Appointments) ──
export interface ArchivedLead {
    id: string; firstName: string | null; lastName: string | null; phone: string | null;
    phone1: string | null; email: string | null; status: string; campaignName: string;
}
export interface ArchivedThread {
    id: string; lastMessageAt: string | null; messageCount: number; status: string;
    leadFirstName: string; leadLastName: string; leadPhone: string; lastMessageBody: string;
}
export interface ArchivedAppointment {
    id: string; appointmentDate: string | null; appointmentTime: string | null;
    status: string; leadFirstName: string; leadLastName: string; campaignName: string;
}

export const getSmsArchivedLeads = (businessId: string) =>
    request<Record<string, unknown>[]>(`/archive/leads?businessId=${businessId}`)
        .then(rows => rows.map(r => snakeToCamel<ArchivedLead>(r)));
export const getSmsArchivedThreads = (businessId: string) =>
    request<Record<string, unknown>[]>(`/archive/threads?businessId=${businessId}`)
        .then(rows => rows.map(r => snakeToCamel<ArchivedThread>(r)));
export const getSmsArchivedAppointments = (businessId: string) =>
    request<Record<string, unknown>[]>(`/archive/appointments?businessId=${businessId}`)
        .then(rows => rows.map(r => snakeToCamel<ArchivedAppointment>(r)));
export const restoreSmsArchived = (type: string, ids: string[], businessId: string) =>
    request<{ success: boolean; restored: number }>(
        `/archive/restore?businessId=${businessId}`,
        { method: "POST", body: JSON.stringify({ type, ids }) },
    );

// ── Timer (now handled via scheduler edge function) ──
export const startSmsFollowUpTimer = () =>
    fetch(`${SUPABASE_URL}/functions/v1/sms-scheduler`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
            "apikey": SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ action: "check_all" }),
    }).then(r => r.json()) as Promise<{ success: boolean; message: string }>;

export const stopSmsFollowUpTimer = () =>
    Promise.resolve({ success: true, message: "Timer is managed by Supabase cron" } as { success: boolean; message: string });

// ── Twilio Numbers ──
import type { TwilioNumber } from "./sms-types";

export const getTwilioNumbers = (businessId: string): Promise<TwilioNumber[]> =>
    request<Record<string, unknown>[]>(`/numbers?businessId=${businessId}`)
        .then(rows => rows.map(r => snakeToCamel<TwilioNumber>(r)));

export const addTwilioNumber = (
    businessId: string,
    phoneNumber: string,
    friendlyName?: string,
    extras?: { label?: string; accountSid?: string; authToken?: string },
): Promise<TwilioNumber> =>
    request<Record<string, unknown>>("/numbers", {
        method: "POST",
        body: JSON.stringify({ businessId, phoneNumber, friendlyName, ...extras }),
    }).then(r => snakeToCamel<TwilioNumber>(r));

export const updateTwilioNumber = (
    id: string,
    data: { label?: string | null; accountSid?: string | null; authToken?: string | null; phoneNumber?: string | null },
): Promise<TwilioNumber> =>
    request<Record<string, unknown>>(`/numbers/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
    }).then(r => snakeToCamel<TwilioNumber>(r));

export const retryErrorLeads = (
    campaignId: string,
    businessId: string,
): Promise<{ success: boolean; retriedCount: number; message: string }> =>
    request(`/campaigns/${campaignId}/retry-errors`, {
        method: "POST",
        body: JSON.stringify({ businessId }),
    });

export const removeTwilioNumber = (id: string): Promise<{ success: boolean }> =>
    request<{ success: boolean }>(`/numbers/${id}`, { method: "DELETE" });

export const getTwilioNumberUsage = (id: string): Promise<TwilioNumberUsage> =>
    request<TwilioNumberUsage>(`/numbers/${id}/usage`);

export const deleteTwilioNumberWithReassignments = (
    id: string,
    plan: ReassignmentPlan,
): Promise<{ success: true; summary: { reassigned: number; removed: number } }> =>
    request(`/numbers/${id}/delete-with-reassignments`, {
        method: "POST",
        body: JSON.stringify(plan),
    });

export const testTwilioNumber = (id: string, businessId: string): Promise<{ success: boolean; message: string }> =>
    request<{ success: boolean; message: string }>(`/numbers/${id}/test`, { method: "POST", body: JSON.stringify({ businessId }) });

export const getTwilioBalance = (id: string, businessId: string): Promise<TwilioBalance> =>
    request<TwilioBalance>(`/numbers/${id}/balance?businessId=${businessId}`);
